/**
 * Filters, and the chip row that drives them.
 *
 * There is one filter state for the whole app: the map, the list and the search
 * overlay all read and write the same object, so a filter set in one place is
 * already set everywhere else. The chip row is rendered into three containers
 * for that reason — same component, same state, three viewports.
 *
 * All five programmes are always offered, including the ones with no venues
 * catalogued yet: they are categories of the project, not a by-product of the
 * current data. Their chips show zero and are disabled.
 */

import { el, fold } from './format.js';
import { GUIDE_ID, isGuide } from './guida.js';

/**
 * Whether the guide's restaurants are in play: only when their chip is on.
 *
 * Off, the map, the list and "Vicino" are what they have always been — the
 * places seen on television — and the guide does not double every count.
 */
function guideOn(filtri) {
  return filtri.programmi.has(GUIDE_ID);
}

/** Empty means "no filter", which is also the initial state. */
export function createFilterState() {
  return { programmi: new Set(), aperto: false, vincitore: false };
}

export function countActiveFilters(filtri) {
  return filtri.programmi.size + (filtri.aperto ? 1 : 0) + (filtri.vincitore ? 1 : 0);
}

export function clearFilters(filtri) {
  filtri.programmi.clear();
  filtri.aperto = false;
  filtri.vincitore = false;
}

/**
 * Filters and searches in one pass. The query looks at the name, the name the
 * place went by on television when it has since changed it, the zone, the
 * street and the programme's display name — the things a visitor would
 * actually type.
 *
 * The address earns its place now that the panel writes one: somebody who
 * remembers a street and not a name had no way in, and "via del Pigneto" is
 * exactly the sort of thing a person types.
 *
 * Both sides are folded, so accents never decide a match. It is the one line
 * here that changes what the catalogue appears to contain: before it, a
 * visitor typing "citta" was told there was nothing.
 */
export function filterVenues(venues, { filtri, q }, programNames) {
  const needle = fold(q).trim();
  const shows = new Set([...filtri.programmi].filter((id) => id !== GUIDE_ID));

  return venues.filter((venue) => {
    // A search that names a starred restaurant finds it with the chip off:
    // somebody typing "Villa Crespi" has said what they want. Only by its own
    // name, or by the guide's — a town alone would bury the town's venues
    // seen on television under its starred ones.
    if (isGuide(venue) && !guideOn(filtri)) {
      if (!needle) return false;
      const own = fold(`${venue.nome ?? ''} ${programNames.get(venue.programma) ?? ''}`);
      if (!own.includes(needle)) return false;
    }
    // The guide's chip adds, it does not narrow: on its own it puts the
    // starred restaurants on top of everything else, as the programmes'
    // chips would not. With a programme chosen too, that programme and the
    // guide.
    if (!isGuide(venue) && shows.size && !shows.has(venue.programma)) return false;
    if (filtri.aperto && venue.stato !== 'aperto') return false;
    if (filtri.vincitore && !venue.vincitore) return false;

    if (needle) {
      const haystack = fold(
        [
          venue.nome ?? '',
          venue.nome_in_onda ?? '',
          venue.zona ?? '',
          venue.indirizzo ?? '',
          programNames.get(venue.programma) ?? '',
        ].join(' ')
      );
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

function chip(label, count, { pressed, disabled = false, ghost = false, extra = '' }) {
  const button = el('button', `${ghost ? 'chip chip--ghost' : 'chip'}${extra ? ` ${extra}` : ''}`);
  button.type = 'button';
  button.disabled = disabled;
  button.setAttribute('aria-pressed', String(pressed));
  button.append(el('span', 'chip__label', label));
  if (count !== null) button.append(el('span', 'chip__n', String(count)));
  return button;
}

/**
 * Rebuilds one chip row.
 *
 * Counts come from the whole catalogue, never from the filtered result: a chip
 * says how many venues it would bring in, which is only useful if the other
 * filters do not shrink it first.
 */
export function renderChips(container, { venues, programs, state, onChange }, { reset = true } = {}) {
  const { filtri } = state;
  const perProgram = (id) => venues.filter((venue) => venue.programma === id).length;
  // "Aperti" and "Vincitori" count what they would show, which leaves the
  // guide out until its own chip brings it in.
  const counted = guideOn(filtri) ? venues : venues.filter((venue) => !isGuide(venue));

  const row = document.createDocumentFragment();

  // "Risulta aperto", not "aperto adesso": the catalogue holds a status we were
  // told once, not opening hours. Saying "now" would claim a fact we lack.
  const open = chip(
    'Aperti',
    counted.filter((venue) => venue.stato === 'aperto').length,
    { pressed: filtri.aperto }
  );
  open.addEventListener('click', () => {
    filtri.aperto = !filtri.aperto;
    onChange();
  });
  row.append(open);

  const winners = chip('Vincitori', counted.filter((venue) => venue.vincitore).length, {
    pressed: filtri.vincitore,
  });
  winners.addEventListener('click', () => {
    filtri.vincitore = !filtri.vincitore;
    onChange();
  });
  row.append(winners);

  // The guide first among the programmes: it replaced a banner above the list
  // that said the same as this chip at three times the size, and at the end of
  // the row it was the one nobody reached. Coloured like the others, which the
  // maintainer preferred to the guide's red.
  const ordered = [
    ...programs.filter((program) => program.id === GUIDE_ID),
    ...programs.filter((program) => program.id !== GUIDE_ID),
  ];
  for (const program of ordered) {
    const count = perProgram(program.id);
    const guide = program.id === GUIDE_ID;
    const button = chip(guide ? '★ Stellati Michelin' : program.nome, count, {
      pressed: filtri.programmi.has(program.id),
      disabled: count === 0,
      extra: guide ? 'chip--guide' : '',
    });
    button.addEventListener('click', () => {
      if (filtri.programmi.has(program.id)) filtri.programmi.delete(program.id);
      else filtri.programmi.add(program.id);
      onChange();
    });
    row.append(button);
  }

  // Left out where a bar of its own already offers it, so the screen never
  // has two "Azzera" doing the same thing.
  if (reset && countActiveFilters(filtri)) {
    const button = chip('Azzera', null, { pressed: false, ghost: true });
    button.addEventListener('click', () => {
      clearFilters(filtri);
      onChange();
    });
    row.append(button);
  }

  container.replaceChildren(row);
}
